import {validatePair, registerPairSelector} from './raith-offers.js';

const currencies = new Set(['GBP', 'USD', 'JPY', 'KRW']);
const text = value => typeof value === 'string' && value.trim() && value.length <= 2000;
const integer = value => Number.isSafeInteger(value) && value >= 0;
const contextKeys = ['productId', 'sectionId', 'formId', 'marketId', 'country', 'language', 'currency'];
const nodes = (root, selector) => [...root.querySelectorAll(selector)];
const only = values => values.length === 1 ? values[0] : null;
const decode = (value, document) => {
  if (!text(value)) throw Error('Missing native formatted text');
  const element = document.createElement('textarea');
  element.innerHTML = value;
  return element.value;
};

export function parseNativePairPacket(serialized, document) {
  try {
    const packet = JSON.parse(serialized);
    if (packet?.version !== 1 || packet.amountDomain !== 'shopify-liquid-money-100' || !/^[a-z0-9][a-z0-9-]{0,254}$/.test(packet.primaryHandle) || !Array.isArray(packet.variantCounts) || packet.variantCounts.length !== 2 || !Array.isArray(packet.totals) || packet.totals.length > 2500) return null;
    if (packet.fixture != null || packet.action != null || packet.config?.fixture != null || packet.config?.action != null) return null;
    const config = packet.config;
    if (!currencies.has(config?.context?.currency) || config.context.scale !== 100) return null;
    for (const item of config.source.items) for (const variant of item.variants) variant.price.formatted = decode(variant.price.formatted, document);
    const model = validatePair(config);
    if (!model || config.context.formId !== `product-form-main-${model.context.productId}-${model.context.sectionId}`) return null;
    for (const [index, item] of model.source.items.entries()) {
      const count = packet.variantCounts[index];
      if (!Number.isSafeInteger(count) || count < 1 || count > 50 || item.variants.length !== count || item.options.some(option => option.values.length > 50)) return null;
      if (['JPY', 'KRW'].includes(model.context.currency) && item.variants.some(variant => variant.price.amount % 100 !== 0)) return null;
    }
    const possible = new Set();
    for (const primary of model.source.items[0].variants) for (const partner of model.source.items[1].variants) {
      const sum = primary.price.amount + partner.price.amount;
      if (!integer(sum)) return null;
      possible.add(sum);
    }
    const totals = new Map();
    for (const total of packet.totals) {
      if (!integer(total?.amount) || !possible.has(total.amount)) return null;
      const formatted = decode(total.formatted, document);
      if (!text(formatted) || (totals.has(total.amount) && totals.get(total.amount) !== formatted)) return null;
      totals.set(total.amount, formatted);
    }
    const immutableConfig = JSON.parse(JSON.stringify({...model, labels: config.labels}));
    return {
      config: immutableConfig,
      primaryHandle: packet.primaryHandle,
      formatTotal({amount, currency, scale}) {
        if (currency !== model.context.currency || scale !== model.context.scale || !integer(amount)) return null;
        return totals.get(amount) || null;
      }
    };
  } catch { return null; }
}

export function registerNativePair(win) {
  if (!win?.customElements || win.customElements.get('raith-offer-native')) return;
  registerPairSelector(win);
  win.customElements.define('raith-offer-native', class extends win.HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new win.AbortController();
      this.navigation = this.navigationIdentity();
      this.suspendedText = null;
      this.epoch = (this.epoch || 0) + 1;
      this.section = this.closest('.shopify-section');
      this.host = only(nodes(this, 'raith-pair-selector'));
      this.hostScript = this.host?.querySelector('script[data-offer-config]');
      if (!this.section || !this.host || !this.hostScript) return;
      const signal = this.controller.signal, document = this.ownerDocument;
      document.addEventListener('product:rerender', event => this.onRerender(event), {capture: true, signal});
      document.addEventListener('change', event => {
        if (event.target.closest?.('form[action*="/localization"]') || event.target.matches?.('[name="country_code"],[name="locale_code"]')) this.revoke();
        else if (this.section.contains(event.target) && event.target.closest?.('variant-picker')) this.revoke();
        else if (this.live && !this.current(this.live)) this.revoke();
      }, {capture: true, signal});
      document.addEventListener('submit', event => {
        if (event.target.matches?.('form[action*="/localization"]')) this.revoke();
      }, {capture: true, signal});
      document.addEventListener('click', event => {
        if (event.target.closest?.('.vl-colours a[href]')) this.revoke();
        else if (this.live && !this.current(this.live)) {
          if (event.target.closest?.('[data-offer-target],raith-offer-native')) { event.preventDefault(); event.stopImmediatePropagation(); }
          this.revoke();
        }
      }, {capture: true, signal});
      document.addEventListener('shopify:section:unload', event => {
        if (event.target === this.section || event.detail?.sectionId === this.dataset.sectionId) { this.revoke(); this.controller.abort(); this.observer?.disconnect(); }
      }, {capture: true, signal});
      win.addEventListener('pagehide', event => {
        this.suspendedText = event.persisted && this.live && this.current(this.live) ? this.live.text : null;
        this.revoke();
      }, {signal});
      win.addEventListener('popstate', () => {
        if (this.navigationIdentity() !== this.navigation || this.live && !this.current(this.live)) {
          this.suspendedText = null; this.revoke();
        }
      }, {signal});
      win.addEventListener('pageshow', event => {
        if (!event.persisted) {if (this.live && !this.current(this.live)) this.revoke(); return;}
        const expectedText = this.suspendedText || this.live?.text;
        this.suspendedText = null;
        // Rebuild from the still-current server packet, never from the old core or
        // selected rows. The document cart controller retains any uncertain write.
        if (!expectedText || this.navigationIdentity() !== this.navigation) {this.revoke(); return;}
        void this.refresh(expectedText);
      }, {signal});
      this.observer = new win.MutationObserver(records => {
        this.retireMutatedSubmission(records);
        const removed = this.live && records.some(record => [...record.removedNodes].some(node => node === this.live.host || node.contains?.(this.live.host)));
        if (this.live && (removed || !this.current(this.live))) this.revoke();
      });
      this.observer.observe(document.documentElement, {attributes: true, attributeOldValue:true, childList: true, characterData: true, subtree: true});
      this.refresh();
    }
    navigationIdentity() {
      const url = new URL(win.location.href), shopify = win.Shopify;
      return JSON.stringify([url.origin, url.pathname, url.searchParams.getAll('view'), url.searchParams.getAll('preview_theme_id'),
        shopify?.shop, shopify?.theme?.id, shopify?.theme?.role, shopify?.routes?.root]);
    }
    marker(root = this.section) {
      return root && only(nodes(root, '[data-offer-current]').filter(marker => marker.dataset.sectionId === this.dataset.sectionId));
    }
    sourceRecord() {
      const marker = this.marker(), packet = marker && only(nodes(marker, 'script[data-offer-native-packet]'));
      if (!marker || !packet) return null;
      const parsed = parseNativePairPacket(packet.textContent, this.ownerDocument);
      if (!parsed) return null;
      const form = only(nodes(this.section, 'form[id]').filter(form => form.getAttribute('id') === parsed.config.context.formId));
      const main = marker.closest('raith-product'), rerender = marker.closest('product-rerender');
      const picker = main && only(nodes(main,'variant-picker').filter(node=>node.getAttribute('form-id')===parsed.config.context.formId));
      const selected = form && only(nodes(form,'input[name="id"]'));
      const cartStamp = only(nodes(this.ownerDocument,'script[data-pack-cart-document]'));
      return {main,rerender,picker,selected,cartStamp,marker, packet, text: packet.textContent, parsed, form, host: this.host, hostScript: this.hostScript};
    }
    retireMutatedSubmission(records) {
      const record = this.live;
      if (!record?.host?.submission) return;
      const document = this.ownerDocument;
      const markerAttrs = ['data-offer-current','data-product-id','data-section-id','data-form-id','data-market-id','data-country','data-language','data-currency','data-product-handle'];
      const exact = new Map([
        [document.documentElement,['lang']], [this,['data-section-id','data-page-market','data-page-country','data-page-currency','data-page-language']],
        [this.section,['id']], [record.marker,markerAttrs], [record.packet,['data-offer-native-packet','type']],
        [record.hostScript,['data-offer-config','type']], [record.form,['id','action','method']],
        [record.main,['data-form-id','data-section-id']], [record.rerender,['observe-form']],
        [record.picker,['form-id','handle']], [record.selected,['name','value','type']], [record.cartStamp,['data-pack-cart-document','type']]
      ]);
      exact.delete(null); exact.delete(undefined);
      const scopes = [
        [this.section,'[data-offer-current],form[id]'], [record.marker,'script[data-offer-native-packet]'],
        [this,'raith-pair-selector'], [record.host,'script[data-offer-config],vl-size-drawer.raith-offer-dialog'],
        [record.main,'variant-picker'], [record.form,'input[name="id"]'], [document.documentElement,'script[data-pack-cart-document],raith-pair-selector,raith-pack-selector']
      ];
      const targets = [...exact.keys(),record.host].filter(Boolean);
      const scripts = [record.packet,record.hostScript,record.cartStamp].filter(Boolean);
      const inScope = (node,scope) => scope && (node===scope || scope.contains(node));
      const relevantAttribute = mutation => {
        const node=mutation.target, name=mutation.attributeName;
        if (exact.get(node)?.includes(name)) return true;
        if (name==='id' && (node.matches('form') && [node.getAttribute('id'),mutation.oldValue].includes(record.parsed.config.context.formId) || node.getAttribute('id')===this.section.id || mutation.oldValue===this.section.id)) return true;
        if (inScope(node,this.section) && (node.matches('form') && name==='id' || name==='data-offer-current' || markerAttrs.includes(name) && node.hasAttribute('data-offer-current'))) return true;
        if (inScope(node,record.main) && node.matches('variant-picker') && ['form-id','handle'].includes(name)) return true;
        if (inScope(node,record.form) && node.matches('input') && ['name','value','type'].includes(name)) return true;
        return name==='data-offer-config' && inScope(node,record.host) || name==='data-offer-native-packet' && inScope(node,record.marker) || name==='data-pack-cart-document';
      };
      const relevantNodes = mutation => [...mutation.removedNodes,...mutation.addedNodes].some(node =>
        targets.some(target=>node===target || node.contains?.(target)) ||
        [node,...(node.querySelectorAll?.('[id]') || [])].some(candidate=>candidate.getAttribute?.('id')===this.section.id || candidate.matches?.('form') && candidate.getAttribute('id')===record.parsed.config.context.formId) ||
        scopes.some(([scope,selector])=>inScope(mutation.target,scope) && (node.matches?.(selector) || node.querySelector?.(selector)))
      );
      if (records.some(mutation => mutation.type==='attributes' ? relevantAttribute(mutation) :
        scripts.some(script=>mutation.target===script || script.contains(mutation.target)) || mutation.type==='childList' && relevantNodes(mutation)
      )) record.host.submission.cancel();
    }
    current(record) {
      this.retireMutatedSubmission(this.observer?.takeRecords() || []);
      if (!record || !this.isConnected || !this.section?.isConnected || record.host !== this.host || only(nodes(this, 'raith-pair-selector')) !== record.host || only(nodes(this.host, 'script[data-offer-config]')) !== record.hostScript || record.hostScript.textContent !== record.hostText) return false;
      const context = record.parsed.config.context, document = this.ownerDocument;
      if (this.marker() !== record.marker || only(nodes(record.marker, 'script[data-offer-native-packet]')) !== record.packet || record.packet.textContent !== record.text || !record.form?.isConnected) return false;
      if (only(nodes(this.section, 'form[id]').filter(form => form.getAttribute('id') === context.formId)) !== record.form) return false;
      if (contextKeys.some(key => record.marker.dataset[key] !== String(context[key]))) return false;
      if (record.marker.dataset.productHandle !== record.parsed.primaryHandle) return false;
      const main = record.marker.closest('raith-product'), rerender = record.marker.closest('product-rerender');
      if (main !== record.main || rerender !== record.rerender || !main || !this.section.contains(main) || !this.section.contains(this) || main.dataset.formId !== context.formId || main.dataset.sectionId !== context.sectionId || rerender?.getAttribute('observe-form') !== context.formId) return false;
      if (this.section.id !== `shopify-section-${context.sectionId}` || this.dataset.sectionId !== context.sectionId) return false;
      if (this.dataset.pageMarket !== context.marketId || this.dataset.pageCountry !== context.country || this.dataset.pageCurrency !== context.currency || this.dataset.pageLanguage !== context.language) return false;
      if (win.Shopify?.country !== context.country || win.Shopify?.currency?.active !== context.currency || win.Shopify?.locale !== context.language || document.documentElement.lang.toLowerCase() !== context.language.toLowerCase()) return false;
      const picker = only(nodes(main, 'variant-picker').filter(picker => picker.getAttribute('form-id') === context.formId));
      if (picker !== record.picker) return false;
      if (picker && picker.getAttribute('handle') !== record.parsed.primaryHandle) return false;
      if (!picker && record.parsed.config.source.items[0].variants.length !== 1) return false;
      const selected = only(nodes(record.form, 'input[name="id"]'));
      return Boolean(selected && selected === record.selected && (selected.value === '' || record.parsed.config.source.items[0].variants.some(variant => variant.id === selected.value)));
    }
    revoke() {
      this.epoch = (this.epoch || 0) + 1;
      this.live = null;
      new Set([this.host, ...nodes(this, 'raith-pair-selector')]).forEach(host => {
        if (!host) return;
        nodes(host, 'script[data-offer-config]').forEach(script => {script.textContent = 'null';});
        host.configure?.(null);
      });
      this.moveStop(false);
    }
    moveStop(active) {
      const main = this.section?.querySelector('raith-product'), stop = main?.querySelector('[data-offer-purchase-stop]');
      const lower = main?.querySelector('[data-offer-entry-wrapper]'), fallback = main?.querySelector('[data-offer-stop-fallback]');
      if (!stop || !fallback) return;
      (active && lower ? lower : fallback).before(stop);
      lower?.closest('.vl-product__editorial')?.classList.toggle('vl-product__editorial--offer', active);
    }
    async refresh(expectedText = null) {
      this.revoke();
      const epoch = this.epoch, record = this.sourceRecord();
      if (!record || (expectedText !== null && record.text !== expectedText)) return false;
      record.hostText = 'null';
      if (!this.current(record)) return false;
      this.live = record;
      await win.customElements.whenDefined('raith-pair-selector');
      if (epoch !== this.epoch || !this.current(record)) return false;
      record.hostText = JSON.stringify(record.parsed.config);
      record.hostScript.textContent = record.hostText;
      const ready = await this.host.configure(record.parsed.config, {formatTotal: record.parsed.formatTotal});
      if (epoch !== this.epoch || !this.current(record)) { if (epoch === this.epoch) this.revoke(); return false; }
      this.moveStop(ready);
      return ready;
    }
    onRerender(event) {
      const marker = this.marker();
      const form = marker && only(nodes(this.section, 'form[id]').filter(form => form.getAttribute('id') === marker.dataset.formId));
      if (!form || event.target !== form || !form.isConnected) return;
      const fragment = event.detail?.htmlFragment, freshMarker = fragment && this.marker(fragment);
      const freshPacket = freshMarker && only(nodes(freshMarker, 'script[data-offer-native-packet]'));
      const text = freshPacket?.textContent;
      const freshHost = fragment && only(nodes(fragment, 'raith-offer-native').filter(host => host.dataset.sectionId === this.dataset.sectionId));
      const clone = event.detail?.productChange === true ? freshHost?.cloneNode(true) : null;
      const entries = clone ? nodes(fragment, '[data-offer-target]').map(entry => (entry.closest('[data-offer-entry-wrapper]') || entry).cloneNode(true)) : [];
      this.revoke();
      const epoch = this.epoch;
      win.queueMicrotask(() => {
        if (!this.isConnected || epoch !== this.epoch || !text || this.marker()?.querySelector('script[data-offer-native-packet]')?.textContent !== text) return;
        if (event.detail?.productChange === true) {
          if (!clone || !parseNativePairPacket(text, this.ownerDocument)) return;
          for (const replacement of entries) {
            const entry = replacement.matches('[data-offer-target]') ? replacement : replacement.querySelector('[data-offer-target]');
            const current = only(nodes(this.section, '[data-offer-target]').filter(node => node.dataset.offerPlacement === entry.dataset.offerPlacement));
            if (current) (current.closest('[data-offer-entry-wrapper]') || current).replaceWith(replacement);
          }
          this.replaceWith(clone);
        } else {
          this.hostScript.textContent = 'null';
          this.refresh(text);
        }
      });
    }
    disconnectedCallback() {
      this.revoke(); this.controller?.abort(); this.observer?.disconnect();
    }
  });
}

if (typeof window !== 'undefined') registerNativePair(window);
